import { z } from "zod";

/** Warnzeichen des eigenen Ersteinschätzungsbogens (Prototyp index.html:535-542, 586-591). */
export const RED_FLAGS = [
  "atemnot",
  "brustschmerz",
  "laehmung_sprachstoerung",
  "bewusstseinstruebung_krampf",
  "blutung",
  "erbrechen",
  "fieber_nackensteife",
  "selbst_fremdgefaehrdung",
] as const;
export const ANLIEGEN = [
  "atemwegsinfekt",
  "harnwege",
  "haut_allergie",
  "magen_darm",
  "folgerezept",
  "krankschreibung",
  "sonstiges",
] as const;
export const BESCHWERDEDAUER = ["unter_24h", "1_3_tage", "4_7_tage", "ueber_1_woche"] as const;
export const SCHWEREGRAD = ["leicht", "mittel", "stark"] as const;

export const URGENCIES = ["rot", "gelb", "gruen"] as const;
export type Urgency = (typeof URGENCIES)[number];
export const TRIAGE_RESULTS = ["geeignet", "nicht_geeignet", "notfall"] as const;
export type TriageResult = (typeof TRIAGE_RESULTS)[number];

/** Sortierrang der Dringlichkeit (Prototyp DRANK): rot < gelb < grün. */
export const urgencyRank = (u: Urgency | null | undefined): number =>
  u === "rot" ? 0 : u === "gelb" ? 1 : 2;

export const questionnaireSchema = z.object({
  redFlags: z.array(z.enum(RED_FLAGS)).max(RED_FLAGS.length),
  minderjaehrig: z.boolean(),
  schwangerOderStillend: z.boolean(),
  anliegen: z.enum(ANLIEGEN),
  dauer: z.enum(BESCHWERDEDAUER),
  schwere: z.enum(SCHWEREGRAD),
  selbstauskunft: z.boolean(),
});
export type QuestionnaireAnswers = z.infer<typeof questionnaireSchema>;

export type ExclusionReason = "minderjaehrig" | "schwanger_stillend";

export type QuestionnaireOutcome =
  | {
      ok: true;
      result: TriageResult;
      urgency: Urgency;
      /** Warnzeichen (bei notfall) bzw. Ausschlussgründe (bei nicht_geeignet) */
      reasons: string[];
    }
  | { ok: false; error: "selbstauskunft_required" };

/**
 * Auswertung wie im Prototyp (index.html:584-616).
 * Hinweis: Schwere „stark“ ergibt – wie im Prototyp – gelb, nicht rot (ärztlich zu prüfen).
 */
export function evaluateQuestionnaire(a: QuestionnaireAnswers): QuestionnaireOutcome {
  const urgency: Urgency = a.schwere === "leicht" ? "gruen" : "gelb";
  if (a.redFlags.length) return { ok: true, result: "notfall", urgency: "rot", reasons: [...a.redFlags] };
  if (!a.selbstauskunft) return { ok: false, error: "selbstauskunft_required" };
  const reasons: ExclusionReason[] = [];
  if (a.minderjaehrig) reasons.push("minderjaehrig");
  if (a.schwangerOderStillend) reasons.push("schwanger_stillend");
  if (reasons.length) return { ok: true, result: "nicht_geeignet", urgency, reasons };
  return { ok: true, result: "geeignet", urgency, reasons: [] };
}

/** Bekannter Patient: Triage entfällt (Prototyp index.html:513). */
export const KNOWN_PATIENT_TRIAGE = {
  result: "geeignet",
  urgency: "gruen",
  anliegen: "folgekontakt",
} as const;
